import { type Answers, QUESTIONS, type Stats } from "../../shared";
import { AnimatedNumber } from "../ui";
import { AnswerOptions } from "./AnswerOptions";

type Props = {
	index: number;
	answers: Answers;
	stats: Stats;
	onVote: (questionId: string, optionIds: string[]) => void;
	onBack: () => void;
	onNext: () => void;
};

function ProgressBar({ index, answers }: { index: number; answers: Answers }) {
	return (
		<div className="flex items-center gap-4">
			<span className="shrink-0 text-sm font-medium text-muted-foreground tabular-nums">
				Frage {index + 1} von {QUESTIONS.length}
			</span>
			<div className="flex flex-1 gap-1.5" aria-hidden="true">
				{QUESTIONS.map((question, i) => (
					<span
						key={question.id}
						className="progress-segment"
						data-state={i === index ? "current" : answers[question.id]?.length ? "answered" : undefined}
					/>
				))}
			</div>
		</div>
	);
}

export function QuestionStep({ index, answers, stats, onVote, onBack, onNext }: Props) {
	const question = QUESTIONS[index];
	if (!question) {
		return null;
	}

	const answer = answers[question.id];
	const answered = !!answer?.length;
	const respondents = stats.questions[question.id]?.respondents ?? 0;
	const isLast = index === QUESTIONS.length - 1;

	return (
		<section className="screen-on">
			<ProgressBar index={index} answers={answers} />

			<div className="mt-8">
				<span className="eyebrow">{question.kicker}</span>
				<h1 className="section-heading mt-4">{question.title}</h1>
				<p className="mt-3 text-base leading-relaxed text-muted-foreground">{question.hint}</p>
			</div>

			<div className="mt-7">
				<AnswerOptions
					question={question}
					answer={answer}
					stats={stats.questions[question.id]}
					onVote={(optionIds) => onVote(question.id, optionIds)}
				/>
			</div>

			<p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
				{answered ? (
					<>
						<span className="live-dot" aria-hidden="true" />
						<span>
							Live aus <AnimatedNumber value={respondents} /> {respondents === 1 ? "Antwort" : "Antworten"} – Sie
							können Ihre Wahl noch ändern.
						</span>
					</>
				) : respondents > 0 ? (
					<span>
						Schon <AnimatedNumber value={respondents} /> {respondents === 1 ? "Antwort" : "Antworten"} – die
						Ergebnisse sehen Sie nach Ihrer.
					</span>
				) : (
					<span>Noch keine Antworten – Sie machen den Anfang.</span>
				)}
			</p>

			<div className="mt-8 flex items-center justify-between gap-3">
				<button type="button" className="btn-secondary" onClick={onBack}>
					Zurück
				</button>
				<div className="flex items-center gap-4">
					{!answered && (
						<button
							type="button"
							className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
							onClick={onNext}
						>
							Überspringen
						</button>
					)}
					<button
						type="button"
						className={isLast ? "btn-launch" : "btn-primary"}
						disabled={!answered}
						onClick={onNext}
					>
						{isLast ? "Abschließen" : "Weiter"}
					</button>
				</div>
			</div>
		</section>
	);
}
